/**
 * widgetSize.js
 *
 * Enum, ordered option list and total normalizers for the per-widget density control
 * (AVA-471; see .claude/plans/WIDGET_SIZE.md). A widget's wizard config stores the picked
 * level as `widgetSize` (canonical) alongside a legacy numeric `fontSize` mirror (0|1|2) so
 * every existing reader keeps working. `resolveWidgetSize` is the single place any reader
 * turns a widget/message object (or a raw value) into one of the three levels; widgets that
 * predate this feature (no widgetSize, only a legacy fontSize or nothing at all) resolve to
 * the level that reproduces their CURRENT on-screen appearance - normal for fontSize-driven
 * families, large for the datagrid family (fontSize was never applied to grids, so a stored
 * number there is meaningless noise, and today's fixed grid look IS the large reference).
 *
 * PURE module - no Vuex, no components, no other project imports.
 */

// The three density levels.
export const WIDGET_SIZE = Object.freeze({
  SMALL: 'small',
  NORMAL: 'normal',
  LARGE: 'large',
});

const VALID_WIDGET_SIZE_IDS = new Set(Object.values(WIDGET_SIZE));

// Ordered for the wizard's toggle: small -> normal -> large.
export const WIDGET_SIZE_OPTIONS = Object.freeze([
  Object.freeze({ id: WIDGET_SIZE.SMALL, label: 'Small' }),
  Object.freeze({ id: WIDGET_SIZE.NORMAL, label: 'Normal' }),
  Object.freeze({ id: WIDGET_SIZE.LARGE, label: 'Large' }),
]);

// Legacy numeric mirror every saved widget still carries (0=Small/1=Normal/2=Large), kept in
// step so any reader that has not migrated to `widgetSize` yet stays correct.
export const LEGACY_FONT_SIZE_BY_WIDGET_SIZE = Object.freeze({
  [WIDGET_SIZE.SMALL]: 0,
  [WIDGET_SIZE.NORMAL]: 1,
  [WIDGET_SIZE.LARGE]: 2,
});

/**
 * Resolve any stored/raw value to a valid WIDGET_SIZE id. Total: never throws, always
 * returns one of the three levels.
 *
 * `source` may be:
 *   - a raw WIDGET_SIZE string ('small' | 'normal' | 'large')
 *   - an object carrying `widgetSize` and/or the legacy `fontSize` (a widget/message/config)
 *   - anything else (null, undefined, a number, {}, an unrecognized string, ...)
 *
 * Resolution order: an explicit, valid `widgetSize` always wins - on the source itself when
 * it is passed as a raw string, or on `source.widgetSize` when it is passed as an object.
 * Otherwise this is a pre-widgetSize (legacy) widget, resolved from `family` + the legacy
 * `fontSize`.
 * @param {*} source
 * @param {{ family?: string }} [options]
 * @returns {string}
 */
export function resolveWidgetSize(source, { family } = {}) {
  if (typeof source === 'string' && VALID_WIDGET_SIZE_IDS.has(source)) return source;

  const widgetSize = source && typeof source === 'object' ? source.widgetSize : undefined;
  if (typeof widgetSize === 'string' && VALID_WIDGET_SIZE_IDS.has(widgetSize)) return widgetSize;

  // Datagrid fontSize was never applied (a stored number there is meaningless noise), and today's fixed grid look IS the large reference, so the legacy default preserves it.
  if (family === 'datagrid') return WIDGET_SIZE.LARGE;

  const fontSize = source && typeof source === 'object' ? source.fontSize : undefined;
  if (fontSize === 0) return WIDGET_SIZE.SMALL;
  if (fontSize === 2) return WIDGET_SIZE.LARGE;
  return WIDGET_SIZE.NORMAL;
}

/**
 * The size modifier class for a given level, or null for `normal` / junk input (no class -
 * today's look, markup untouched). Normalizes its input first (via `resolveWidgetSize` with
 * no family - by the time a size is being classed it is expected to already be resolved, not
 * a legacy value needing family-aware fallback). Total: never throws.
 * @param {*} size
 * @param {string} prefix
 * @returns {string|null}
 */
export function widgetSizeClass(size, prefix) {
  const normalized = resolveWidgetSize(size);
  return normalized === WIDGET_SIZE.NORMAL ? null : `${prefix}--${normalized}`;
}
